import { styled, theme } from '@/src/stitches.config'
import Image from 'next/image'
import LinkButton from '@/src/common/components/marketing/buttons/LinkButton'
import CopyPromoCodeButton from '@/src/common/components/marketing/buttons/CopyPromoCodeButton'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfo } from '@fortawesome/free-solid-svg-icons/faInfo'
import DefaultModal from '@/src/common/components/marketing/modals/DefaultModal'
import { useState } from 'react'
import handleEnterPressEvent from '@/src/common/utilities/handleEnterPressEvent'
import Button from '@/src/common/components/marketing/buttons/Button'
import { useRouter } from 'next/router'
import { formatDate2DigitMonthDayYear } from '@/src/common/utilities/dateFormatters'

const ItemWrapper = styled('div', {
    fontFamily: theme.fonts.default,
    display: 'flex',
    fd: 'column',
    ai: 'center',
    jc: 'space-between',
    width: '100%',
    p: 10,
    textAlign: 'center !important',
    maxWidth: 400,
    margin: 'auto',
    img: {
        width: '100%',
        height: 'auto',
        br: 10,
        cursor: 'pointer',
        aspectRatio: '250/164',
        objectFit: 'cover',
    },
    '& p': {
        my: 8,
    },
    '@media (min-width: 640px)': {
        margin: 'unset',
    },
    '@media (min-width: 1383px)': {
        maxWidth: 335,
    },
    '.headerText': {
        cursor: 'pointer',
        fontWeight: 'bold',
        fontSize: '1.1rem',
        mt: 20,
    },
})

const OfferTerms = styled('p', {
    position: 'relative',
    cursor: 'pointer',
    svg: {
        position: 'absolute',
        right: -25,
        top: -1,
        fontSize: '.75rem',
        border: '1px solid $dark',
        ml: 5,
        py: 2,
        px: 6,
        br: 20,
    },
    '&:hover': {
        textDecoration: 'underline',
        svg: {
            background: '$primaryLight',
            borderColor: '$primary',
            color: '$primary',
        },
    },
})

export interface IPromoItem {
    promotionHeader: string
    promoCode: any
    promoTerms: any
    linkUrl: string
    startDate: Date
    endDate: Date
    image: string
    promoId?: string
}

function copyCodeAndCloseModal(
    setIsCopiedExternally: Function,
    setShowModal: Function
) {
    setIsCopiedExternally(true)
    setShowModal(false)
}

const PromoItem = ({
    promotionHeader,
    promoTerms,
    promoCode,
    startDate,
    endDate,
    image,
    linkUrl,
}: IPromoItem) => {
    const router = useRouter()
    const [showModal, setShowModal] = useState<boolean>(false)
    const [isCopiedExternally, setIsCopiedExternally] = useState<boolean>(false)
    const dateRange =
        startDate && endDate
            ? `${formatDate2DigitMonthDayYear(
                  new Date(startDate)
              )} - ${formatDate2DigitMonthDayYear(new Date(endDate))}`
            : null

    return (
        <ItemWrapper>
            {promoTerms && (
                <DefaultModal
                    showModal={showModal}
                    setShowModal={setShowModal}
                    testId={'promoTerms-modal'}
                >
                    <div
                        dangerouslySetInnerHTML={{
                            __html: promoTerms,
                        }}
                    />
                    <Button
                        variant={'primary'}
                        label={'Okay'}
                        textTransform={'uppercase'}
                        showArrow={true}
                        width={'standard'}
                        onClick={() =>
                            copyCodeAndCloseModal(
                                setIsCopiedExternally,
                                setShowModal
                            )
                        }
                        onKeyDown={(e) =>
                            handleEnterPressEvent(e, () =>
                                copyCodeAndCloseModal(
                                    setIsCopiedExternally,
                                    setShowModal
                                )
                            )
                        }
                    />
                </DefaultModal>
            )}
            <Image
                src={image ?? ''}
                alt={'Promo Image'}
                height={210}
                width={340}
                onClick={() => router.push(linkUrl)}
                onKeyDown={(e) =>
                    handleEnterPressEvent(e, () => router.push(linkUrl))
                }
                tabIndex={0}
            />
            <p
                className={'headerText'}
                onClick={() => router.push(linkUrl)}
                onKeyDown={(e) =>
                    handleEnterPressEvent(e, () => router.push(linkUrl))
                }
            >
                {promotionHeader}
            </p>
            {dateRange && <p>Valid {dateRange}</p>}
            {promoTerms && (
                <OfferTerms
                    tabIndex={0}
                    onClick={() => setShowModal(true)}
                    onKeyDown={(e) =>
                        handleEnterPressEvent(e, () => setShowModal(true))
                    }
                >
                    Offer Details <FontAwesomeIcon icon={faInfo} />
                </OfferTerms>
            )}
            {promoCode && (
                <CopyPromoCodeButton
                    promoCode={promoCode}
                    style={{ width: '100%', marginTop: 12, marginBottom: 12 }}
                    isCopiedExternally={isCopiedExternally}
                    setIsCopiedExternally={setIsCopiedExternally}
                />
            )}
            {linkUrl && (
                <LinkButton
                    variant={'primary'}
                    label={'Shop now'}
                    url={linkUrl}
                    textTransform={'uppercase'}
                    buttonWidth={'100%'}
                    size={'sm'}
                />
            )}
        </ItemWrapper>
    )
}

export default PromoItem
